import React, { ReactNode, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { signInInitialValues, signInSchema } from 'constants/schemaValidations';
import AuthLeftPanel from '../Common/AuthLeftPanel';
import AuthHeadingPanel from '../Common/AuthHeadingPanel';
import { logEmailLogin, useLoginMutation } from 'services';
import { SignInInterface } from 'constants/interfaces';
import { USER_TYPES } from 'constants/index';
import { setPreviewsRole, setUserInfo } from 'store/auth';
import useTranslate from 'hooks/useTranslate';
import { setTempLoginData } from 'store/user';
import { getBrowserID } from 'utils';
import SocialLogin from '../Common/SocialLogin';
import { secureStorage } from 'utils/secureStorage';

const Login: React.FC = () => {
	const { translate } = useTranslate();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const userType = searchParams.get('userType');
	// mutation
	const { mutateAsync: loginMutation, isPending } = useLoginMutation();

	const [showPassword, setShowPassword] = useState(false);

	const togglePasswordVisibility = () => {
		setShowPassword((prev) => !prev);
	};

	// storage
	const loginInfo = secureStorage.getItem('loginInfo') || {};

	const formik = useFormik({
		initialValues: { ...signInInitialValues, ...loginInfo },
		validationSchema: signInSchema(translate),
		onSubmit: async (values) => {
			const fcmDeviceToken = secureStorage.getItem('fcmToken') || '';
			loginMutation({
				...values,
				browserToken: getBrowserID(),
				userType: userType || USER_TYPES.SEEKER,
				deviceToken: fcmDeviceToken || '',
			})
				.then((res: any) => {
					if (res?.success) {
						if (formik.values.rememberMe) {
							secureStorage.setItem('loginInfo', {
								email: values.email,
								rememberMe: formik.values.rememberMe,
							});
						} else {
							secureStorage.removeItem('loginInfo');
						}

						if (res?.data?.user?.checkTwoFactorAuth) {
							setTempLoginData(res?.data);
							navigate('/two-factor-authentication');
							return;
						}
						setUserInfo(res?.data);
						setPreviewsRole(res?.data?.user?.userType);

						if (res?.data?.user?.userType === USER_TYPES.COMPANY) {
							navigate(`/company/profile`);
							return;
						} else {
							// if (res?.data?.user?.preference) {
								navigate(`/seeker/profile`);
								return;
							// } else {
							// 	navigate(`/seeker/preference`);
							// 	return;
							// }
						}
					}
				})
				.finally(() => {
					logEmailLogin();
				});
		},
	});
	const renderError = <T extends keyof SignInInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<div className="auth bg-white d-flex flex-column flex-lg-row">
			<div className="auth-left-content">
				<AuthLeftPanel />
			</div>
			<div className="auth-right-content flex-1 d-flex align-items-center justify-content-center position-relative">
				<div className="auth-form mb-0 mt-5 mx-3 my-md-5 m-md-5">
					{/* <AuthHeadingPanel description={translate('Auth.authHeadingLoginPanelDescription')} /> */}
					{userType !== USER_TYPES?.COMPANY && (
						<>
							<SocialLogin type="login" />
							<div className="or-separator d-flex">
								<p className="mb-0 rounded-circle text-center">{translate('Common.or')}</p>
							</div>
						</>
					)}
					<div className="auth-form-fields">
						<Form className="form" onSubmit={formik.handleSubmit}>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.email')}</Form.Label>
								<Form.Control
									type="email"
									placeholder={translate('PlaceHolder.emailText1')}
									name="email"
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.email}
									autoComplete="off"
								/>
								{renderError('email')}
							</Form.Group>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.password')}</Form.Label>
								<Form.Control
									type={showPassword ? 'text' : 'password'}
									name="password"
									placeholder={translate('PlaceHolder.passwordText', { field: '8' })}
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.password}
									autoComplete="off"
								/>
								<i
									className={`ri-eye${!showPassword ? '-off' : ''}-line form-icon position-absolute lh-1`}
									onClick={() => togglePasswordVisibility()}
								></i>
								{renderError('password')}
							</Form.Group>
							<div className="d-flex align-items-center justify-content-between">
								<Form.Check
									type="checkbox"
									className="mb-0"
									name="rememberMe"
									label={translate('PlaceHolder.rememberMe')}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									checked={formik.values.rememberMe}
									id="rememberMeLogin"
								></Form.Check>
								<Link
									to={`/forgot-password?userType=${userType || USER_TYPES.SEEKER}`}
									className="forgot-password"
								>
									{translate('Common.forgotPassword')}
								</Link>
							</div>
							<div className="form-btn">
								<Button
									className="filled-btn rounded-pill w-100"
									type="submit"
									disabled={isPending}
								>
									{translate('Common.login')}
								</Button>
							</div>
							<p className="mb-0 text-center">
								{translate('Common.doNotHaveAccount')}{' '}
								<Link
									to={`/signup?userType=${userType || USER_TYPES.SEEKER}`}
									className="fw-bold join-link"
								>
									{translate('Common.joinNow')}
								</Link>
							</p>
						</Form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Login;
